<script setup lang="ts">
import { IInvoice, IInvoiceLine, ProductType, SectionItem } from '@posx/core';
import { storeToRefs } from 'pinia';
import { computed, inject, onMounted, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import { useEmployeeStore } from '@/stores/employee';
import { useConfigStore } from '@/stores/config';
import { preciseRound } from '@/utils';
import { getModifierDisplayFactor } from '@/utils/inventory';
import { formatNumber, formatCurrency, formatWeight } from '@/utils/formatNumber';
import { resolveSalespersons } from '@/utils/salesperson';

const employeeStore = useEmployeeStore();
const configStore = useConfigStore();
const { currentStaff } = storeToRefs(employeeStore);
const appConfig = computed(() => configStore.appConfig);
const orderFontSize = computed(() => `${appConfig.value?.config?.order?.order_font_size || 14}px`);

const { t, locale } = useI18n();

const invoice = inject<Ref<IInvoice | null>>('invoice', ref(null));
const sectionItem = inject<Ref<SectionItem | null>>('sectionItem', ref(null));
const filteredLines = computed(() => {
  if (appConfig.value?.config?.order?.hide_combo_item_on_receipt) {
    return invoice.value?.lines?.filter((line) => !line.is_combo_item) ?? [];
  }
  return invoice.value?.lines ?? [];
});
// Display weight in grams for by_weight items
const formatQty = (line: IInvoiceLine) => line.item?.type === ProductType.ByWeight ? formatWeight(line.quantity) : line.quantity;


onMounted(async () => {
  await configStore.startLiveQuery();
});
</script>

<template>
  <div class="h-full scroll-container receipt-container" :style="{ fontSize: orderFontSize }">
    <div class="flex justify-between">
      <span>{{ t('receipt.companyName') }}</span>
      <span>{{ appConfig?.config?.general?.company_name }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.address') }}</span>
      <span>{{ appConfig?.config?.general?.company_address }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.tel') }}</span>
      <span>{{ appConfig?.config?.general?.company_tel }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.gstNo') }}</span>
      <span>{{ appConfig?.config?.general?.company_tax_reg_no }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.ref_id') }}</span>
      <span>#{{ invoice?.ref_id }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.order_num') }}</span>
      <span>{{ invoice?.call_num }}</span>
    </div>

    <div v-if="invoice?.remark" class="flex justify-between">
      <span class="text-gray-600 italic">{{ t('table.remark') }}：{{ invoice.remark }}</span>
    </div>

    <div class="flex justify-between">
      <span>{{ t('receipt.table') }}</span>
      <span>{{ invoice?.table_name }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.employee') }}</span>
      <span>{{ currentStaff?.name }}</span>
    </div>
    <div v-for="sp in resolveSalespersons(invoice ?? undefined)" :key="sp.uid" class="flex justify-between">
      <span>{{ t('receipt.salesperson') }}</span>
      <span>{{ sp.name }} ({{ formatCurrency(sp.commission_amount) }})</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.pax') }}</span>
      <span>{{ invoice?.pax }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.open') }}</span>
      <span>{{ invoice?.humanized_created_at }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.close') }}</span>
      <span>{{ invoice?.humanized_paid_at }}</span>
    </div>
    <hr class="mt-2 border-black border-dashed mb-2" />
    <el-row>
      <el-col :span="4">{{ t('receipt.qty') }}</el-col>
      <el-col :span="13">{{ t('receipt.item') }}</el-col>
      <el-col :span="7" class="text-right">{{ t('receipt.amt') }}</el-col>
    </el-row>
    <hr class="mt-2 border-black border-dashed mb-2" />
    <el-row v-for="line in filteredLines">
      <el-col :span="24">
        <el-row>
          <el-col :span="4">{{ formatQty(line) }}</el-col>
          <el-col :span="13">{{ line.item.name_translations?.[locale] || line.item.name }}</el-col>
          <el-col :span="7" class="text-right">
            <span :class="{ 'line-through': line.discount_amount }">{{ formatCurrency(line.subtotal_before_discount) }}</span>
            <span v-if="line.discount_amount" class="ml-1">{{ formatCurrency(line.subtotal) }}</span>
          </el-col>
        </el-row>
        <!--规格-->
        <el-row v-for="mod in line.modifiers">
          <el-col :span="13" :offset="4">-{{ mod.item.name_translations?.[locale] || mod.item.name }}</el-col>
          <el-col :span="7" class="text-right">+{{ formatCurrency(preciseRound(mod.price * mod.quantity * getModifierDisplayFactor(line.quantity))) }}</el-col>
        </el-row>
        <!--折扣-->
        <el-row v-if="line.discount_amount">
          <el-col :span="13" :offset="4">-{{ t('receipt.discount') }}</el-col>
          <el-col :span="7" class="text-right">-{{ formatCurrency(line.discount_amount) }}</el-col>
        </el-row>
        <!--备注-->
        <el-row v-if="line.remark">
          <el-col :span="13" :offset="4" class="text-gray-600 italic">{{ line.remark }}</el-col>
        </el-row>
        <!--BCRS deposit-->
        <el-row v-if="line.item.has_bcrs_deposit">
          <el-col :span="13" :offset="4" class="text-gray-600">{{ t('receipt.deposit_bcrs') }}</el-col>
        </el-row>
        <!--销售人员-->
        <el-row v-for="sp in resolveSalespersons(line)" :key="sp.uid">
          <el-col :span="13" :offset="4" class="text-gray-600">{{ t('receipt.salesperson') }}: {{ sp.name }} ({{ formatCurrency(sp.commission_amount) }})</el-col>
        </el-row>
      </el-col>
    </el-row>
    <hr class="my-2 border-black border-dashed" />
    <div class="flex justify-between">
      <span>{{ t('receipt.subTotal') }}</span>
      <span>{{ formatCurrency(invoice?.subtotal ?? 0) }}</span>
    </div>
    <!-- <div class="flex justify-between">
      <span>{{ t("receipt.pkgDeduction") }}</span>
      <span>$ {{ 0 }}</span>
    </div> -->
    <div class="flex justify-between" v-if="invoice?.service_charge">
      <span>{{ t('receipt.service_charge') }}</span>
      <span>{{ formatCurrency(invoice?.service_charge ?? 0) }}</span>
    </div>

    <!-- Display all discounts from the discounts array -->
    <div v-for="discount in invoice?.discounts" class="flex justify-between">
      <span>
        {{ discount.name === 'custom' ?
          (discount.calc_type === 'flat' ? `-${formatCurrency(discount.amount)}` : `${parseFloat((discount.percent * 100).toFixed(2))}% Off`) :
          discount.name }}
      </span>
      <span v-if="discount.calc_type === 'flat'">-{{ formatCurrency(discount.amount) }}</span>
      <span v-else>-{{ formatCurrency(discount.percent_amount) }}</span>
    </div>

    <div class="flex justify-between" v-if="invoice?.minus_credit">
      <span>{{ t('receipt.minus_credit') }}</span>
      <span>-{{ formatCurrency(invoice?.minus_credit ?? 0) }}</span>
    </div>

    <div class="flex justify-between" v-if="invoice?.tax">
      <span>{{ appConfig?.config?.tax?.name?.toUpperCase?.() ?? 'GST' }}</span>
      <span>{{ formatCurrency(invoice?.tax ?? 0) }}</span>
    </div>
    <!-- BCRS deposit is added after tax, so it is GST exempt -->
    <div class="flex justify-between" v-if="invoice?.bcrs_deposit">
      <span>{{ t('receipt.bcrs_deposit') }}</span>
      <span>{{ formatCurrency(invoice?.bcrs_deposit ?? 0) }}</span>
    </div>
    <div class="flex justify-between" v-if="invoice?.tip">
      <span>{{ t('receipt.tip') }}</span>
      <span>{{ formatCurrency(invoice?.tip ?? 0) }}</span>
    </div>
    <!-- Rounding placed right above TOTAL per accounting convention -->
    <div class="flex justify-between" v-if="invoice?.rounding">
      <span>{{ t('receipt.rounding') }}</span>
      <span>{{ formatCurrency(invoice?.rounding ?? 0) }}</span>
    </div>
    <div class="flex justify-between">
      <span>{{ t('receipt.total') }}</span>
      <span>{{ formatCurrency(invoice?.grand_total ?? 0) }}</span>
    </div>

    <!-- Payment Information -->
    <hr class="my-2 border-black border-dashed" v-if="invoice?.payments?.length" />
    <div v-for="payment in invoice?.payments" class="flex justify-between">
      <span>{{ t('receipt.payment_by') }}{{ payment.payment_method?.name_translations?.[locale] || payment.payment_method?.name }}</span>
      <span>{{ formatCurrency(payment.tender_amount - (payment.change_amount || 0)) }}</span>
    </div>

    <hr class="my-2 border-black border-dashed" />

    <!-- Customer Information Section -->
    <div v-if="invoice?.customer_id && invoice?.customer_id !== 0" class="my-3">
      <div class="flex justify-between">
        <span>{{ t('receipt.customer_tel') }}</span>
        <span>{{ invoice?.customer?.phone_number }}</span>
      </div>

      <!-- Credit and Point Operations -->
      <div v-if="invoice.add_credit > 0" class="flex justify-between">
        <span>{{ t('receipt.top_up') }}</span>
        <span>{{ formatCurrency(invoice.add_credit) }}</span>
      </div>
      <div v-if="invoice.minus_credit > 0" class="flex justify-between">
        <span>{{ t('receipt.deducted_credit') }}</span>
        <span>{{ formatCurrency(invoice.minus_credit) }}</span>
      </div>
      <div v-if="invoice.rewarded_credit > 0" class="flex justify-between">
        <span>{{ t('receipt.cash_back') }}</span>
        <span>{{ formatCurrency(invoice.rewarded_credit) }}</span>
      </div>
      <div v-if="invoice.add_point > 0" class="flex justify-between">
        <span>{{ t('receipt.earned_points') }}</span>
        <span>{{ formatNumber(invoice.add_point, 0) }}pt</span>
      </div>
      <div v-if="invoice.minus_point > 0" class="flex justify-between">
        <span>{{ t('receipt.deducted_points') }}</span>
        <span>{{ formatNumber(invoice.minus_point, 0) }}pt</span>
      </div>
      <div v-if="invoice.rewarded_point > 0" class="flex justify-between">
        <span>{{ t('receipt.rewarded_points') }}</span>
        <span>{{ formatNumber(invoice.rewarded_point, 0) }}pt</span>
      </div>

      <div class="flex justify-between">
        <span>{{ t('receipt.credit_balance') }}</span>
        <span>{{ formatCurrency(preciseRound(invoice.customer.credit - invoice.minus_credit + invoice.add_credit + invoice.rewarded_credit)) }}</span>
      </div>
      <div class="flex justify-between">
        <span>{{ t('receipt.point_balance') }}</span>
        <span>{{ formatNumber(invoice.customer.point - invoice.minus_point + invoice.add_point + invoice.rewarded_point, 0) }}pt</span>
      </div>

      <hr class="my-2 border-black border-dashed" />
    </div>

    <div class="my-3 text-center">{{ t('receipt.thank') }}</div>
  </div>
</template>

<style scoped>
.receipt-container {
  /* Hide scrollbar for webkit browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}
.receipt-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

</style>
